import { Button, Flex } from "antd";
import type { CreateTypes } from "canvas-confetti";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import ReactCanvasConfetti from "react-canvas-confetti";
import { Link, useParams } from "react-router";
import { useWindowSize } from "react-use";
import { useGetCertificateQuery } from "../../../../app/features/courses/courses.api";
import DownloadIcon from "../../../../assets/icons/DownloadIcon";
import LeftArrow from "../../../../assets/icons/LeftArrow";
import ShareIcon from "../../../../assets/icons/ShareIcon";
import ShareSocialMedia from "../../../../components/ShareSocialMedia";
import { ENV_CONFIG } from "../../../../config/global.config";
import useAuth from "../../../../hooks/useAuth";
import useDownloadPdf from "../../../../hooks/useDownloadPdf";
import useError from "../../../../hooks/useError";
import { cn } from "../../../../utils/global.utils";
import { enToBnNumber } from "../../../../utils/number.utils";
import { convertSeconds } from "../../../../utils/time.utils";
import CompleteIcon from "../_icons/CompleteIcon";
import CertificateV2 from "./CertificateV2";
import CertificateViewLoading from "./CertificateViewLoading";

type ConfettiInstance = {
  confetti: CreateTypes;
};
export default function CertificateView() {
  const { id } = useParams();
  const confettiRef = useRef<ConfettiInstance | null>(null);
  const { width, height } = useWindowSize();
  const [hasPlayedConfetti, setHasPlayedConfetti] = useState(false);
  // const [showConfetti, setShowConfetti] = useState(false);
  const { employee } = useAuth();
  const { data, isLoading, error } = useGetCertificateQuery(id as string, {
    skip: !id,
  });
  useError(error, "সার্টিফিকেট লোড করা যায়নি");
  // Download certificate hook
  const { pdfRef, isDownloading, downloadPdf } = useDownloadPdf();

  const courseDetails = JSON.parse(data?.payloadJson || "{}");
  const shareLink = `${ENV_CONFIG.FRONTEND_URL}/verify-certificate/${courseDetails?.certificateNo}`;

  const fireConfetti = () => {
    confettiRef.current?.confetti({
      particleCount: 120,
      spread: 80,
      startVelocity: 45,
      gravity: 0.9,
      ticks: 300,
      origin: { x: 0.5, y: 0.5 },
    });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Fire confetti when data loads (only once)
  useEffect(() => {
    if (!isLoading && data && !hasPlayedConfetti && confettiRef.current) {
      // Small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        fireConfetti();
        setHasPlayedConfetti(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isLoading, data, hasPlayedConfetti]);

  const courseName = courseDetails?.course?.title || "Course";
  const handleDownload = () => {
    const fileName = `${courseName}_Certificate`;

    downloadPdf({ fileName, title: "সার্টিফিকেট" });
  };

  const time = convertSeconds(courseDetails?.progress?.totalWatchSec || 0);

  if (isLoading) {
    return <CertificateViewLoading />;
  }

  return (
    <section className="bg-[#F5F5F5]  py-10">
      <div className="container">
        {/* Confetti Canvas */}
        {/* {showConfetti && (
        <div className="overflow-x-hidden">
          <Confetti
            width={Math.max(0, width - 20)}
            height={height}
            numberOfPieces={200}
          />
        </div>
      )} */}
        <ReactCanvasConfetti
          onInit={(instance) => {
            confettiRef.current = instance;
          }}
          style={{
            position: "fixed",
            pointerEvents: "none",
            width,
            height,
            top: 0,
            left: 0,
            zIndex: 9999,
          }}
        />

        <Link
          to="/user/dashboard"
          className="!text-theme-primary flex items-center gap-2"
        >
          <LeftArrow />{" "}
          <span className="text-Base text-lg font-medium">হোমে ফিরে যান</span>
        </Link>

        <div className="max-w-[990px] pt-10 pb-20 mx-auto">
          <div className="space-y-11">
            <Flex justify="center">
              <Button
                className="
    size-28 
    rounded-full 
    !bg-[#E9F2F2] 
    !border-[4px] 
    !border-white 
    flex 
    justify-center 
    items-center 
    animate-water
    transition-all 
    duration-150
    hover:scale-105
    active:scale-95
  "
                onClick={fireConfetti}
              >
                <CompleteIcon />
              </Button>
            </Flex>

            <div className="space-y-2">
              <h2 className="text-center text-theme-neutral-800 text-5xl font-bold">
                অভিনন্দন{" "}
                <span className="inline-block relative -top-1">🎉</span>
              </h2>
              <p className="text-center text-theme-slate-700 text-xl font-normal">
                সফলভাবে আপনি কোর্সটি সম্পন্ন করেছেন
              </p>
              <h5 className="text-center text-Base text-3xl font-semibold text-theme-primary">
                {courseName}
              </h5>
            </div>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <ScoreCard
                  score={
                    enToBnNumber(courseDetails?.score?.scorePercent || 0) + "%"
                  }
                  title="স্কোর"
                  classNames={{
                    title: "text-theme-primary",
                    score: "text-theme-primary",
                  }}
                />
                <ScoreCard
                  score={enToBnNumber(time?.convertedTime || 0)}
                  title={`${time?.type === "H" ? "ঘণ্টা" : "মিনিট"} সময় লেগেছে`}
                  classNames={{
                    title: "text-theme-yellow-600",
                    score: "text-theme-yellow-600",
                  }}
                />
              </div>

              <Flex align="center" justify="space-between" wrap>
                <h3 className="text-center text-theme-neutral-800 text-2xl font-semibold">
                  সার্টিফিকেট
                </h3>

                <Flex align="center" gap={24}>
                  <Button
                    variant="outlined"
                    type="primary"
                    size="large"
                    className={cn("rounded-full min-w-48")}
                    classNames={{ icon: "mr-2" }}
                    icon={<DownloadIcon />}
                    onClick={handleDownload}
                    loading={isDownloading}
                  >
                    <span className="relative top-0.5">ডাউনলোড করুন</span>
                  </Button>
                  <ShareSocialMedia url={shareLink}>
                    <ShareIcon className="text-theme-slate-400" />
                  </ShareSocialMedia>
                </Flex>
              </Flex>

              <div className="bg-white shadow overflow-x-auto">
                <div ref={pdfRef} className="w-[990px]">
                  {/* <Certificate
                    name={
                      employee?.fullName || employee?.email || "Unknown User"
                    }
                    courseName={
                      courseDetails?.course?.title || "Unknown Course"
                    }
                    facilitatorName="Nuzmus Shakib"
                    issueDate={
                      courseDetails?.issuedAt
                        ? dayjs(courseDetails?.issuedAt).format("MMMM DD, YYYY")
                        : "Unknown Date"
                    }
                  /> */}
                  <CertificateV2
                    name={employee?.fullName || employee?.email}
                    courseName={courseDetails?.course?.title}
                    issueDate={
                      courseDetails?.issuedAt
                        ? dayjs(courseDetails?.issuedAt).format("MMMM DD, YYYY")
                        : undefined
                    }
                    passportNumber={employee?.passportNumber}
                    qrCode={shareLink}
                  />
                </div>
              </div>

              {/* <SubmitReview /> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

type TScoreCard = {
  score: string;
  title: string;
  classNames?: {
    root?: string;
    score?: string;
    title?: string;
  };
};
const ScoreCard = ({ score, title, classNames }: TScoreCard) => (
  <div
    className={cn(
      "p-6 bg-white rounded-xl border border-theme-zinc-200",
      classNames?.root,
    )}
  >
    <h2
      className={cn(
        "text-center text-Base text-6xl font-medium mb-2",
        classNames?.score,
      )}
    >
      {score}
    </h2>
    <p
      className={cn(
        "text-center text-Base text-lg font-medium",
        classNames?.title,
      )}
    >
      {title}
    </p>
  </div>
);
